import type { ScreenImageTransitionModeId } from '@/constants/screenImageTransition'
import type { AnimationClip } from '@/types/animationClip'
import type { Vec3 } from '@/types/vec3'

export type { Vec3 } from '@/types/vec3'

/** 屏幕贴图媒体类型（由上传文件与工程 JSON 共同决定）。 */
export type ScreenMediaType = 'none' | 'image' | 'video'

/** 多图屏幕栈单项（运行时 `textureSrc` 为 blob URL，持久化仅存 `id` + `fileName`）。 */
export interface ScreenImageStackItem {
  id: string
  fileName: string
  /** `URL.createObjectURL`；工程加载后为 `null` 直至用户重新选文件 */
  textureSrc: string | null
}

/**
 * 时间轴关键帧：记录该时刻的变换快照（与动画编辑器一致）。
 */
export interface AnimationKeyframe {
  id: string
  /** 时间轴时刻（秒） */
  timeSeconds: number
  position: Vec3
  /** 度 */
  rotationDeg: Vec3
  scale: Vec3
  /** v1 迁移用；缺省按 1 */
  materialOpacity?: number
}

export interface AnimationLayer {
  id: string
  /** 列表展示名 */
  name: string
  /** 图层是否锁定；锁定后禁止编辑操作。 */
  locked: boolean
  /** 左侧库中对应的机型模板 id */
  deviceTemplateId: string
  position: Vec3
  /** 度 */
  rotationDeg: Vec3
  scale: Vec3
  /** 屏幕贴图文件名；未上传时为 `null` */
  screenMaterialFileName: string | null
  /**
   * 屏幕贴图对象 URL（`URL.createObjectURL`）；与文件名同步，卸载图层或替换文件时需 `revokeObjectURL`。
   */
  screenTextureSrc: string | null
  /** 当前屏幕贴图媒体类型；无贴图时为 `none`。 */
  screenMediaType: ScreenMediaType
  /**
   * 时间轴秒数偏移：`videoTime = clamp(globalTime + offset, 0, duration)`。
   */
  screenVideoTimelineOffsetSec: number
  /**
   * 多静态图屏幕栈；`screenMediaType === 'video'` 时须为空数组。
   * 单图兼容：可为仅含一项的栈，此时 `screenTextureSrc` 应为 `null`，纹理只存于栈项。
   */
  screenImages: ScreenImageStackItem[]
  /**
   * 多图栈栅格切换 GPU 过渡模式（`-1`～`6`）；缺省时运行态按 {@link resolvedLayerScreenImageTransitionMode} 视为淡入淡出。
   */
  screenImageTransitionMode?: ScreenImageTransitionModeId
  /**
   * 该图层时间轴动画片段（同层区间互不重叠）。
   * 工程 JSON v1 的顶层 `keyframes` 在加载时会迁移为单条 clip。
   */
  clips: AnimationClip[]
  /** 图层在总时间轴上的可见起点（秒）。 */
  visibilityStartTimeSec: number
  /** 图层在总时间轴上的可见终点（秒）。 */
  visibilityEndTimeSec: number
  /**
   * 运行时可见状态（由时间轴与可见区间计算得出）。
   * 注意：该字段会在预览/导出时被实时覆盖，不应作为持久化真值来源。
   */
  visible: boolean
  /**
   * 可见性运行时扩展字段预留（例如后续淡入淡出）。
   */
  visibilityRuntime?: {
    fadeInSec?: number
    fadeOutSec?: number
  }
  /**
   * 文字图层内容（仅 `deviceTemplateId === 'text-plane'` 时有效）。
   * 默认值为 `'Animock'`。
   */
  textContent?: string
  /**
   * 文字图层字号（px，运行时映射为 Canvas 绘制大小，仅 `text-plane` 有效）。
   * 默认值为 `64`。
   */
  textFontSize?: number
  /**
   * 文字图层颜色（CSS 颜色字符串，仅 `text-plane` 有效）。
   * 默认值为 `'#888888'`（经典灰）。
   */
  textColor?: string
  /**
   * 文字图层字重（仅 `text-plane` 有效）。
   * 仅支持 `'normal'` 与 `'bold'` 两档，默认 `'bold'`。
   */
  textFontWeight?: 'normal' | 'bold'
  /**
   * 文字图层对齐方式（仅 `text-plane` 有效）。
   * 支持 `'left' | 'center' | 'right'`，默认 `'center'`。
   */
  textAlign?: 'left' | 'center' | 'right'
}

/**
 * 新建图层时屏幕贴图相关字段的默认值。
 */
export function defaultScreenMaterialFields(): Pick<
  AnimationLayer,
  | 'screenMaterialFileName'
  | 'screenTextureSrc'
  | 'screenMediaType'
  | 'screenVideoTimelineOffsetSec'
  | 'screenImages'
> {
  return {
    screenMaterialFileName: null,
    screenTextureSrc: null,
    screenMediaType: 'none',
    screenVideoTimelineOffsetSec: 0,
    screenImages: [],
  }
}

/**
 * 新建图层时可见区间相关字段默认值。
 *
 * @param durationSec - 当前时间轴总时长（秒）
 */
export function defaultVisibilityFields(
  durationSec: number,
): Pick<AnimationLayer, 'visibilityStartTimeSec' | 'visibilityEndTimeSec' | 'visible'> {
  const normalizedDuration = Number.isFinite(durationSec) ? Math.max(0, durationSec) : 0
  return {
    visibilityStartTimeSec: 0,
    visibilityEndTimeSec: normalizedDuration,
    visible: true,
  }
}
